import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FiFileText, FiDollarSign, FiMail, FiBell, FiTrendingUp } from 'react-icons/fi';
import { api } from '../../utils/api';
import './AdminStats.css';

const AdminStats = () => {
  const [stats, setStats] = useState(null);
  const [recentContacts, setRecentContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, contactsRes] = await Promise.all([
        api.getStats(),
        api.getRecentContacts()
      ]);
      setStats(statsRes.data);
      setRecentContacts(contactsRes.data);
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { 
      key: 'services', 
      icon: FiFileText, 
      value: stats?.services || 0, 
      label: 'Services',
      color: '#2d5016',
      gradient: 'linear-gradient(135deg, rgba(45, 80, 22, 0.1), rgba(74, 124, 42, 0.1))'
    },
    { 
      key: 'pricing', 
      icon: FiDollarSign, 
      value: stats?.pricing || 0, 
      label: 'Tarifs',
      color: '#4a7c2a',
      gradient: 'linear-gradient(135deg, rgba(74, 124, 42, 0.1), rgba(107, 142, 63, 0.1))'
    },
    { 
      key: 'contacts', 
      icon: FiMail, 
      value: stats?.contacts || 0, 
      label: 'Messages reçus',
      color: '#6b8e3f',
      gradient: 'linear-gradient(135deg, rgba(107, 142, 63, 0.1), rgba(168, 201, 127, 0.1))'
    },
    { 
      key: 'newContacts', 
      icon: FiBell, 
      value: stats?.newContacts || 0, 
      label: 'Nouveaux messages',
      color: '#a8c97f',
      gradient: 'linear-gradient(135deg, rgba(168, 201, 127, 0.1), rgba(168, 201, 127, 0.15))',
      highlight: true
    }
  ];

  if (loading) {
    return (
      <div className="loading-admin">
        <div className="loading-spinner"></div>
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <div className="admin-stats">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h2>Tableau de bord</h2>
      </motion.div>

      {stats && (
        <motion.div
          className="stats-grid"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.key}
                className={`stat-card ${stat.highlight ? 'highlight' : ''}`}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ 
                  y: -8, 
                  scale: 1.03,
                  boxShadow: "0 16px 48px rgba(0, 0, 0, 0.12)"
                }}
              >
                <div className="stat-icon" style={{ '--stat-color': stat.color, '--stat-gradient': stat.gradient }}>
                  <Icon />
                </div>
                <div className="stat-content">
                  <h3>{stat.value}</h3>
                  <p>{stat.label}</p>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      )}

      <motion.div
        className="recent-contacts"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
      >
        <div className="recent-contacts-header">
          <h3>Messages récents</h3>
          {recentContacts.length > 0 && (
            <span className="contacts-count">{recentContacts.length}</span>
          )}
        </div>
        {recentContacts.length > 0 ? (
          <div className="contacts-list">
            {recentContacts.map((contact, index) => (
              <motion.div
                key={contact._id}
                className="contact-item"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                whileHover={{ x: 4, boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)" }}
              >
                <div className="contact-header">
                  <div>
                    <strong>{contact.name}</strong>
                    <span className="contact-email">{contact.email}</span>
                  </div>
                  <span className={`status-badge ${contact.status}`}>
                    {contact.status === 'new' ? 'Nouveau' : 
                     contact.status === 'read' ? 'Lu' : 
                     contact.status === 'replied' ? 'Répondu' : 'Archivé'}
                  </span>
                </div>
                <p className="contact-subject">{contact.subject}</p>
                <p className="contact-message">{contact.message.substring(0, 100)}...</p>
                <span className="contact-date">
                  {new Date(contact.createdAt).toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="no-data">
            <FiMail className="no-data-icon" />
            <p>Aucun message récent</p>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AdminStats;
